import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { useParams, useHistory } from 'react-router-dom'
import { Container, Spinner } from 'react-bootstrap'

import { useApi, useBreakpoint, useStrickyHeader } from '../../../hooks'
import useAppContext from '../../app'
import VehicleHeader from './VehicleHeader'
import AdasSections from './AdasSections'
import MechanicalSection from './MechanicalSection'
import SafetyFeatures from './SafetyFeatures'

import './adasReportDetail.css'

const AdasReportDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const { adasReportService } = useApi()
  const { store: { clientSettings, isLog } } = useAppContext()
  const { BREAKPOINT, screenBiggerThan } = useBreakpoint()
  const isBiggerThanXL = screenBiggerThan(BREAKPOINT.XL)

  // Expand/Collapse All lives here, not in AdasSections, because MechanicalSection
  // is a SIBLING of it — the buttons render in the ADAS card's header but have to
  // drive both cards. They previously reached only the ADAS sections, so the
  // Mechanical card silently ignored them.
  //
  // This is a DEFAULT broadcast, not a lock: each card seeds its own open map from
  // it, so a user can still collapse one section afterwards. The nonce is bumped
  // on every press so pressing the same button twice re-applies it (#37249).
  const [expandAll, setExpandAll] = useState(true)
  const [expandNonce, setExpandNonce] = useState(0)
  const applyExpandAll = (next) => {
    setExpandAll(next)
    setExpandNonce((n) => n + 1)
  }

  // Offset the section headers pin at. Measured rather than hardcoded: the nav
  // has no declared height, it gains 3px of active-tab border on this route, and
  // for share visitors with no session it is not sticky at all (offset 0). Set
  // once here as a custom property so both cards below inherit it.
  const stickyTop = useStrickyHeader('header-app')

  const defaultAllowViewPricing = clientSettings && clientSettings.vehicleReports && clientSettings.vehicleReports.allowViewPricing
  let allowViewPricing = defaultAllowViewPricing
  if (!isLog) {
    const token = localStorage['access_token']
    const payload = token ? JSON.parse(window.atob(token.split('.')[1])) : null
    if (typeof payload?.pricing === 'boolean') allowViewPricing = payload.pricing
  }

  const { isLoading, isError, data: reportResult, refetch } = useQuery(
    ['adasReport', id],
    () => adasReportService.get(id),
    { refetchOnWindowFocus: false }
  )

  const report = reportResult && reportResult.vehicleReport
  const allowViewPricingByReport = reportResult && typeof reportResult.allowViewPricing === 'boolean'
    ? reportResult.allowViewPricing
    : allowViewPricing

  const handleBack = () => {
    history.push('/adas-reports')
  }

  if (isLoading) {
    return (
      <Container fluid={!isBiggerThanXL} className='my-5 text-center'>
        <Spinner animation='border' />
      </Container>
    )
  }

  // Fetch finished but the report couldn't be loaded (not found, not owned, or
  // a server error). Show an actionable message instead of an endless spinner.
  if (isError || !report) {
    return (
      <Container fluid={!isBiggerThanXL} className='my-5 text-center'>
        <div className='adas-detail__top-bar'>
          <button type='button' className='adas-detail__back' onClick={handleBack} aria-label='Back to Reports'>
            <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M15 19L8 12L15 5' stroke='#000000' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        </div>
        <p className='mt-4 mb-3'>We couldn&apos;t load this report. It may still be processing, or you may not have access to it.</p>
        <button className='btn btn-outline-primary' onClick={() => refetch()}>Try again</button>
      </Container>
    )
  }

  return (
    <Container fluid={!isBiggerThanXL} className='adas-report-detail'>
      <div
        className='adas-detail__content'
        style={{ '--adas-sticky-top': `${stickyTop}px` }}
      >
        <VehicleHeader report={report} allowViewPricing={allowViewPricingByReport} refetch={refetch} onBack={handleBack} />
        <SafetyFeatures report={report} />
        <AdasSections
          report={report}
          allowViewPricing={allowViewPricingByReport}
          expandAll={expandAll}
          expandNonce={expandNonce}
          onExpandAll={applyExpandAll}
        />
        <MechanicalSection
          report={report}
          allowViewPricing={allowViewPricingByReport}
          expandAll={expandAll}
          expandNonce={expandNonce}
        />
      </div>
    </Container>
  )
}

export default AdasReportDetail
